import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Container, Fade, Paper, Typography, useTheme } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { clearTrips, fetchMoreTrips, fetchTrips } from '@acorn-portal/shared/src/store/telematicsTripsSlice';
import { TripDateSection, groupTripsByDate } from '@acorn-portal/shared/src/components/telematics/trip';
import { TelematicsErrorPanel } from '@acorn-portal/shared/src/components/telematics/common';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { useDriverScoreVisibility } from '@acorn-portal/shared/src/hooks/useDriverScoreVisibility';
import { useLoadMoreOnScroll } from './use-load-more-on-scroll';

/** Trips fetched per page. Larger pages mean fewer round trips for long histories. */
const TRIPS_PAGE_SIZE = 10;

/**
 * Recent trips page. Lists a policy's trips grouped by day with infinite scroll.
 *
 * Data flow: the first page is fetched once per policy number; further pages are
 * fetched by `useLoadMoreOnScroll` as the sentinel below the list comes into view.
 * A failed first page shows a full error panel; a failed later page keeps the
 * trips already loaded and shows an inline retry.
 */
const TripListPage: React.FC = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useAppDispatch();
	const policyNumber = useAppSelector((state) => state.fullPolicy.policyNumber);
	const { items, loading, loadingMore, error, hasNextPage, pageNumber } = useAppSelector(
		(state) => state.telematicsTrips,
	);
	const showDriverScore = useDriverScoreVisibility();

	/** The policy number a first-page fetch has been dispatched for. Until it matches, the page is pending. */
	const [requestedPolicyNumber, setRequestedPolicyNumber] = useState<string | null>(null);

	const fetchFirstPage = useCallback(() => {
		if (!policyNumber) return;
		dispatch(clearTrips());
		dispatch(fetchTrips({ policyReference: policyNumber, pageNumber: 1, pageSize: TRIPS_PAGE_SIZE }));
	}, [dispatch, policyNumber]);

	const fetchNextPage = useCallback(() => {
		if (!policyNumber) return;
		dispatch(fetchMoreTrips({ policyReference: policyNumber, pageNumber: pageNumber + 1, pageSize: TRIPS_PAGE_SIZE }));
	}, [dispatch, policyNumber, pageNumber]);

	// First page: once per policy number, as soon as it is known
	useEffect(() => {
		if (!policyNumber || requestedPolicyNumber === policyNumber) return;
		setRequestedPolicyNumber(policyNumber);
		fetchFirstPage();
	}, [policyNumber, requestedPolicyNumber, fetchFirstPage]);

	const groupedTrips = useMemo(() => groupTripsByDate(items), [items]);

	const hasTrips = items.length > 0;
	const isPending = loading || requestedPolicyNumber !== policyNumber;
	const isInitialLoading = isPending && !hasTrips;
	const hasInitialError = !isPending && Boolean(error) && !hasTrips;
	const hasLoadMoreError = Boolean(error) && hasTrips;
	const isEmpty = !isPending && !error && !hasTrips;
	const canAutoLoadMore = hasNextPage && !isPending && !loadingMore && !error;

	const sentinelRef = useLoadMoreOnScroll({ onLoadMore: fetchNextPage, enabled: canAutoLoadMore });

	const handleBack = useCallback(() => {
		navigate(location.state?.from ?? ROUTE_PATHS.home, { state: { from: location.pathname } });
	}, [navigate, location.state?.from, location.pathname]);

	const handleViewTrip = useCallback(
		(tripId: string) => {
			navigate(ROUTE_PATHS.telematicsTripDetails.replace(':tripId', tripId), { state: { from: location.pathname } });
		},
		[navigate, location.pathname],
	);

	return (
		<Fade in timeout={400}>
			<Paper
				elevation={1}
				sx={{
					margin: { xs: theme.spacing(1), sm: theme.spacing(1), md: theme.spacing(3) },
					borderRadius: '4px',
					padding: { xs: theme.spacing(2), sm: theme.spacing(2), md: theme.spacing(3) },
					display: 'flex',
					flexDirection: 'column',
					backgroundColor: theme.palette.background.paper,
				}}
			>
				<Container maxWidth='sm' disableGutters>
					<Button
						startIcon={<ArrowBackIcon />}
						onClick={handleBack}
						sx={{
							color: theme.palette.secondary.main,
							textTransform: 'none',
							mb: 2,
							pl: 0,
							'&:hover': { backgroundColor: 'transparent' },
						}}
					>
						BACK
					</Button>

					<Typography variant='h5' component='h1' fontWeight='bold' gutterBottom color='secondary.main'>
						Recent trips
					</Typography>
					<Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
						{showDriverScore
							? 'View your recent trips to learn more about them and their individual scores.'
							: 'View your recent trips to learn more about them.'}
					</Typography>

					{isInitialLoading && (
						<Box display='flex' justifyContent='center' py={4}>
							<CircularProgress />
						</Box>
					)}

					{hasInitialError && (
						<TelematicsErrorPanel
							title='Unable to load your trips'
							message="We couldn't load your recent trips at the moment. Please try again or come back later."
							onRetry={fetchFirstPage}
							onBack={handleBack}
						/>
					)}

					{isEmpty && (
						<Typography align='center' color='text.secondary'>
							No trips recorded yet.
						</Typography>
					)}

					{hasTrips && (
						<Box>
							{Array.from(groupedTrips.entries()).map(([dateLabel, trips]) => (
								<TripDateSection
									key={dateLabel}
									dateLabel={dateLabel}
									trips={trips}
									onViewScoreBreakdown={handleViewTrip}
									showDriverScore={showDriverScore}
								/>
							))}

							{/* Infinite-scroll sentinel */}
							<Box ref={sentinelRef} data-testid='load-more-sentinel' aria-hidden sx={{ height: 1 }} />

							{loadingMore && (
								<Box display='flex' justifyContent='center' py={2}>
									<CircularProgress size={24} />
								</Box>
							)}

							{hasLoadMoreError && (
								<Box display='flex' flexDirection='column' alignItems='center' gap={1} py={2}>
									<Typography variant='body2' color='error' role='alert'>
										We couldn&apos;t load more trips.
									</Typography>
									<Button variant='outlined' size='small' onClick={fetchNextPage} sx={{ textTransform: 'none' }}>
										Try again
									</Button>
								</Box>
							)}

							{!hasNextPage && !error && (
								<Typography variant='body2' color='text.secondary' align='center' sx={{ py: 2 }}>
									No more trips to load
								</Typography>
							)}
						</Box>
					)}
				</Container>
			</Paper>
		</Fade>
	);
};

export default TripListPage;
